// Component: MobileAlgoDashboard.jsx
// Purpose: Phone-tier Algo Dashboard — the running-bots MONITORING cockpit. A thin
// PRESENTER: all live data (deployments/positions/performance over WS+REST) and
// the stop handler arrive from AlgoDashboard.jsx (the container) as props —
// nothing is re-fetched here, the same container/presenter split as
// MobileWatchList. Layout:
//   • aggregate P&L hero + Active / Win rate / Trades
//   • All / Live / Paper filter (drives the container's mode)
//   • running-first algo list; each row: status dot · name · symbol · mode badge,
//     position-or-meta subtitle, live P&L + LTP trailing (TickFlash on updates)
//   • swipe a RUNNING row → Stop (confirm-gated, real stopDeployment)
//   • tap a row → deployment detail
import React, { useState } from "react";
import { NumberHero, StatTile, ListRow, SwipeRow, Sheet, TickFlash, PullToRefresh } from "../ds";
import { useDeploymentLtp } from "../../contexts/TradingDataContext";
//* E5b (issue/warmup-recovery-gates-not-independent) — the ONLY place this row may derive
//  "something's wrong"; the desktop card and the (unused today) StrategyRow call the same
//  helper so the three surfaces can never drift.
import { deriveLivenessNote, livenessToneColor } from "../../pages/AlgoTrading/utils/liveness";

const mono = { fontFamily: "var(--font-mono, ui-monospace, monospace)", fontVariantNumeric: "tabular-nums" };

// Signed INR, whole rupees (P&L). "+₹4,427" / "−₹1,240".
const inr = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Math.round(n);
  return `${v < 0 ? "−" : "+"}₹${Math.abs(v).toLocaleString("en-IN")}`;
};
// Plain price, 2dp.
const price = (n) =>
  n == null || n === "" || isNaN(n)
    ? "—"
    : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ago(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const pnlColor = (n) => (n > 0 ? "var(--color-profit)" : n < 0 ? "var(--color-loss)" : "var(--fg-1)");
const hasOpenPos = (p) => !!(p && p.side && p.side !== "FLAT" && p.status !== "CLOSED");

const MODES = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "paper", label: "Paper" },
];

// Recessed segmented filter (neutral — no accent), mirrors the WatchList pills.
function ModeFilter({ value, onChange }) {
  return (
    <div style={{ display: "inline-flex", gap: 4, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 10, padding: 3 }}>
      {MODES.map((m) => {
        const active = value === m.value;
        return (
          <button
            key={m.value}
            type="button"
            onClick={() => onChange?.(m.value)}
            style={{
              appearance: "none", cursor: "pointer", border: "none",
              fontSize: 12, fontWeight: 500, padding: "5px 14px", borderRadius: 7,
              background: active ? "var(--bg-3)" : "transparent",
              color: active ? "var(--fg-1)" : "var(--fg-3)",
            }}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

const StopGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
);

// One algo (deployment) row. Reads its own live LTP via useDeploymentLtp — the
// same hook the desktop card uses — so the price ticks with no extra plumbing.
function AlgoRow({ s, last, onOpen, onStopRequest }) {
  const running = s.status === "running";
  const live = useDeploymentLtp(s.id); // hook runs for every row (rules-of-hooks); stopped → inert
  const pos = s.position;
  const openPos = hasOpenPos(pos);
  //--> The ONE liveness label (utils/liveness.js) — null only for non-running cards.
  const livenessNote = deriveLivenessNote(s);

  const runningSubtitle = livenessNote
    ? (
      <>
        {s.timeframe} · started {ago(s.startTime)}{" "}
        <span style={{ color: livenessToneColor(livenessNote.tone) }}>
          · {livenessNote.text}
        </span>
      </>
    )
    : `${s.timeframe} · started ${ago(s.startTime)}`;

  const subtitle = openPos
    ? `${pos.side} ${pos.qty ?? pos.quantity ?? ""} @ ₹${price(pos.avg_entry_price)} · ${s.timeframe}`
    : running
      ? runningSubtitle
      : `Stopped · ${ago(s.stoppedAt)}`;

  const face = (
    <ListRow
      onClick={() => onOpen?.(s.id)}
      leading={
        <span
          aria-hidden="true"
          style={{
            width: 8, height: 8, borderRadius: 4, flex: "0 0 auto",
            background: running ? "var(--color-profit)" : "var(--color-loss)",
            boxShadow: running ? "0 0 0 3px rgb(var(--color-profit-rgb) / 0.2)" : "none",
          }}
        />
      }
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
          {s.symbol && (
            <span style={{ fontSize: 9, fontWeight: 600, color: "var(--fg-3)", border: "1px solid var(--border)", padding: "1px 5px", borderRadius: 3, letterSpacing: ".05em", flex: "0 0 auto" }}>{s.symbol}</span>
          )}
          <span className={`ds-badge xs ${s.mode === "live" ? "profit" : "info"}`} style={{ flex: "0 0 auto" }}>
            {s.mode === "live" ? "Live" : "Paper"}
          </span>
        </span>
      }
      subtitle={subtitle}
      trailing={
        <TickFlash value={s.pnlUnresolved ? 0 : s.pnl} sign="auto" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, borderRadius: 6, padding: "1px 4px", margin: "-1px -4px" }}>
          {/* 144-1: an unresolved net is a placeholder, never Rs 0 */}
          <span style={{ ...mono, fontSize: 14, fontWeight: 600, color: s.pnlUnresolved ? "var(--fg-3)" : pnlColor(s.pnl) }}>{s.pnlUnresolved ? "Unresolved" : inr(s.pnl)}</span>
          <span style={{ ...mono, fontSize: 11, color: "var(--fg-3)" }}>
            {running && live?.ltp != null ? `${live.direction === "down" ? "▼" : "▲"} ${price(live.ltp)}` : `${s.totalTrades} trades`}
          </span>
        </TickFlash>
      }
    />
  );

  const border = last ? undefined : { borderBottom: "1px solid var(--border)" };

  // Only running algos are swipeable (Stop). Stopped rows are dimmed, static.
  if (!running) return <div style={{ ...border, opacity: 0.6 }}>{face}</div>;

  return (
    <SwipeRow style={border} actions={[{ key: "stop", label: "Stop", tone: "danger", icon: StopGlyph, onClick: () => onStopRequest(s) }]}>
      {face}
    </SwipeRow>
  );
}

function StopConfirmSheet({ target, onCancel, onConfirm }) {
  const openPos = hasOpenPos(target?.position);
  return (
    <Sheet open={!!target} onClose={onCancel} title="Stop algo">
      <p style={{ fontSize: 14, color: "var(--fg-2)", lineHeight: 1.5, margin: "0 0 4px" }}>
        Stop <b style={{ color: "var(--fg-1)" }}>{target?.name}</b>? It stops placing new orders immediately.
      </p>
      {openPos && (
        <p style={{ fontSize: 12.5, color: "var(--color-warning, #D97706)", lineHeight: 1.5, margin: "8px 0 0" }}>
          This algo has an open {target.position.side} position — close it first, or the stop will be blocked.
        </p>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <button type="button" onClick={onCancel} style={{ flex: 1, appearance: "none", cursor: "pointer", height: 46, borderRadius: 12, background: "var(--bg-3)", border: "1px solid var(--border)", color: "var(--fg-1)", fontSize: 14, fontWeight: 600 }}>
          Cancel
        </button>
        <button type="button" onClick={() => onConfirm(target)} style={{ flex: 1, appearance: "none", cursor: "pointer", height: 46, borderRadius: 12, background: "var(--color-loss)", border: "none", color: "#fff", fontSize: 14, fontWeight: 600 }}>
          Stop algo
        </button>
      </div>
    </Sheet>
  );
}

export default function MobileAlgoDashboard({
  strategies = [],
  summary = {},
  mode = "all",
  onMode,
  loading = false,
  error = null,
  onOpen,
  onStop,
  onRefresh,
}) {
  const [stopTarget, setStopTarget] = useState(null);
  const handleRefresh = onRefresh || (() => new Promise((res) => setTimeout(res, 600)));

  const stats = [
    { k: "Active", v: String(summary.activeStrategies ?? 0) },
    { k: "Win rate", v: `${Math.round(summary.winRate ?? 0)}%` },
    { k: "Trades", v: String(summary.totalTrades ?? 0) },
  ];

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div style={{ padding: "0 16px", color: "var(--fg-1)" }}>
        {/* Aggregate P&L hero — colored by sign (this screen's whole job is "are my
            bots green or red"); the sub-line splits Live vs Paper. */}
        <div style={{ marginBottom: 20, paddingTop: 2 }}>
          {summary.pnlUnresolvedCount > 0 && (
            <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 6 }}>
              Net P&amp;L is unresolved (charges unresolved) for {summary.pnlUnresolvedCount}{" "}
              {summary.pnlUnresolvedCount === 1 ? "strategy" : "strategies"} - the totals exclude{" "}
              {summary.pnlUnresolvedCount === 1 ? "it" : "them"}.
            </div>
          )}
          <NumberHero eyebrow="All algos · Total P&L" value={<span style={{ color: pnlColor(summary.totalPnl ?? 0) }}>{inr(summary.totalPnl ?? 0)}</span>}>
            <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
              Live {inr(summary.livePnl ?? 0)} · Paper {inr(summary.paperPnl ?? 0)}
            </span>
          </NumberHero>
          <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
            {stats.map((s) => <StatTile key={s.k} eyebrow={s.k} value={s.v} size="lg" />)}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <ModeFilter value={mode} onChange={onMode} />
        </div>

        {loading ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "var(--fg-3)", fontSize: 13 }}>Loading algos…</div>
        ) : error ? (
          <div style={{ padding: "40px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-loss)", marginBottom: 4 }}>Couldn't load algos</div>
            <div style={{ fontSize: 12, color: "var(--fg-3)" }}>{String(error)}</div>
          </div>
        ) : strategies.length === 0 ? (
          <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 12, padding: "40px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No algos deployed</div>
            <div style={{ fontSize: 12, color: "var(--fg-3)" }}>Build a strategy on desktop, then deploy it to see it here.</div>
          </div>
        ) : (
          <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {strategies.map((s, i) => (
              <AlgoRow key={s.id} s={s} last={i === strategies.length - 1} onOpen={onOpen} onStopRequest={setStopTarget} />
            ))}
          </div>
        )}

        {strategies.some((s) => s.status === "running") && (
          <div style={{ textAlign: "center", marginTop: 14, fontSize: 10, color: "var(--fg-3)", letterSpacing: ".03em" }}>
            Swipe a running algo left to Stop
          </div>
        )}

        <StopConfirmSheet target={stopTarget} onCancel={() => setStopTarget(null)} onConfirm={(s) => { onStop?.(s); setStopTarget(null); }} />
      </div>
    </PullToRefresh>
  );
}
